import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';

const CHUNK_RECOVERY_KEY = 'itechs:last-chunk-recovery';
const CHUNK_RECOVERY_COOLDOWN_MS = 30_000;

const isStaleChunkError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('failed to fetch dynamically imported module')
    || message.includes('error loading dynamically imported module')
    || message.includes('loading chunk')
    || message.includes('importing a module script failed');
};

const reloadWithFreshApplicationFiles = async ({ force = false } = {}) => {
  const now = Date.now();
  const lastRecovery = Number(window.sessionStorage.getItem(CHUNK_RECOVERY_KEY) || 0);
  if (!force && now - lastRecovery < CHUNK_RECOVERY_COOLDOWN_MS) return false;

  window.sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(now));
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)));
  }
  if ('caches' in window) {
    const cacheKeys = await window.caches.keys().catch(() => []);
    await Promise.all(cacheKeys.map((cacheKey) => window.caches.delete(cacheKey).catch(() => false)));
  }

  const freshUrl = new URL(window.location.href);
  freshUrl.searchParams.set('__fresh', String(now));
  window.location.replace(freshUrl.toString());
  return true;
};

const LoginPage = lazy(() => import('./pages/LoginPage'));
const StudentPage = lazy(() => import('./pages/StudentPage'));
const StudentMaterialPage = lazy(() => import('./pages/StudentMaterialPage'));
const StudentGameLevelsPage = lazy(() => import('./pages/StudentGameLevelsPage'));
const StudentGamePlayPage = lazy(() => import('./pages/StudentGamePlayPage'));
const TeacherPage = lazy(() => import('./pages/TeacherPage'));
const TeacherAssessmentEditorPage = lazy(() => import('./pages/TeacherAssessmentEditorPage'));
const TeacherAssessmentProgressPage = lazy(() => import('./pages/TeacherAssessmentProgressPage'));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage'));

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application Error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    if (isStaleChunkError(error)) {
      reloadWithFreshApplicationFiles().catch(() => {});
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h1>
            <p className="text-gray-600 mb-6">We apologize for the inconvenience. Please try reloading the page.</p>
            {this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-mono text-red-800 break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            <button 
              onClick={() => reloadWithFreshApplicationFiles({ force: true })}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-all"
            >
              Reload Fresh Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading component for route transitions
const RouteLoader = () => (
  <div>
    <LoadingSpinner />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div>
            <Suspense fallback={<RouteLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LoginPage />} />
                <Route path="/login" element={<LoginPage />} />
                
                {/* Student Routes */}
                <Route
                  path="/student"
                  element={
                    <ProtectedRoute allowedRoles={['STUDENT']}>
                      <StudentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/materials/:materialId"
                  element={
                    <ProtectedRoute allowedRoles={['STUDENT']}>
                      <StudentMaterialPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/materials/:materialId/quiz"
                  element={
                    <ProtectedRoute allowedRoles={['STUDENT']}>
                      <StudentMaterialPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/materials/:materialId/result/:attemptId"
                  element={
                    <ProtectedRoute allowedRoles={['STUDENT']}>
                      <StudentMaterialPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/games/:gameType"
                  element={
                    <ProtectedRoute allowedRoles={['STUDENT']}>
                      <StudentGameLevelsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/games/:gameType/levels/:levelNumber/play"
                  element={
                    <ProtectedRoute allowedRoles={['STUDENT']}>
                      <StudentGamePlayPage />
                    </ProtectedRoute>
                  }
                />

                {/* Teacher Routes */}
                <Route
                  path="/teacher"
                  element={
                    <ProtectedRoute allowedRoles={['TEACHER', 'SUPER_ADMIN']}>
                      <TeacherPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessments/edit/:materialId"
                  element={
                    <ProtectedRoute allowedRoles={['TEACHER']}>
                      <TeacherAssessmentEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessments/progress/:viewMode"
                  element={
                    <ProtectedRoute allowedRoles={['TEACHER']}>
                      <TeacherAssessmentProgressPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teacher/assessments/progress/:viewMode/:studentId"
                  element={
                    <ProtectedRoute allowedRoles={['TEACHER']}>
                      <TeacherAssessmentProgressPage />
                    </ProtectedRoute>
                  }
                />
                {/* Super Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                      <SuperAdminPage />
                    </ProtectedRoute>
                  }
                />
                {/* Role-based redirects */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <RoleBasedRedirect />
                    </ProtectedRoute>
                  }
                />

                {/* Catch all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>

            {/* Toast notifications */}
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <RouteLoader />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const roleRedirects = {
    STUDENT: '/student',
    TEACHER: '/teacher',
    SUPER_ADMIN: '/admin',
  };

  const redirectTo = roleRedirects[user.role] || '/';
  return <Navigate to={redirectTo} replace />;
};

// 404 Not Found component
const NotFound = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
      <div className="text-6xl mb-4">🔍</div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
      <p className="text-gray-600 mb-6">The page you're looking for doesn't exist or has been moved.</p>
      <div className="flex gap-3">
        <button 
          onClick={() => window.history.back()}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-all"
        >
          Go Back
        </button>
        <a 
          href="/"
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-all text-center"
        >
          Go Home
        </a>
      </div>
    </div>
  </div>
);

export default App;
